import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useD328Query } from './hooks/useD328Query';
import type { ReportRow } from '../../services/d328Api';
import './D328Form.css';

const D328Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = useD328Query();

  const [filterMode, setFilterMode] = useState<number>(2);
  const [orderNoFrom, setOrderNoFrom] = useState('');
  const [orderNoTo, setOrderNoTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const handleQuery = () => {
    runQuery({
      filter_mode: filterMode,
      order_no_from: orderNoFrom.trim() || undefined,
      order_no_to: orderNoTo.trim() || undefined,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setFilterMode(2);
    setOrderNoFrom('');
    setOrderNoTo('');
    setDateFrom('');
    setDateTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>異常入庫單說明明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}@media print{body{margin:10mm}}</style></head><body>
<h2>異常入庫單說明明細表</h2>
<table><thead><tr><th>入庫單號</th><th>入庫日期</th><th>單據類型</th><th>說明1</th><th>說明2</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.order_no}</td><td>${r.order_date}</td><td>${r.order_type}</td><td>${r.memo1 || ''}</td><td>${r.memo2 || ''}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '入庫單號', dataIndex: 'order_no', key: 'order_no', width: 120 },
    { title: '入庫日期', dataIndex: 'order_date', key: 'order_date', width: 110 },
    { title: '單據類型', dataIndex: 'order_type', key: 'order_type', width: 100 },
    { title: '說明1', dataIndex: 'memo1', key: 'memo1', ellipsis: true },
    { title: '說明2', dataIndex: 'memo2', key: 'memo2', ellipsis: true },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.D328} action="query">
      <div className="d328-container">
        <div className="d328-header">
          <h2>異常入庫單說明明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('資料篩選')}
            <Radio.Group value={filterMode} onChange={e => setFilterMode(e.target.value)}>
              <Radio.Button value={1}>全部</Radio.Button>
              <Radio.Button value={2}>未說明</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('入庫單號起')}
              <Input value={orderNoFrom} onChange={e => setOrderNoFrom(e.target.value)} placeholder="起" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫單號迄')}
              <Input value={orderNoTo} onChange={e => setOrderNoTo(e.target.value)} placeholder="迄" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 150 }} allowClear />
            </div>
            <div>
              {filterLabel('入庫日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 150 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<ReportRow>
            columns={columns}
            dataSource={data}
            rowKey={(r, i) => `${r.order_no}-${r.order_type}-${i}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 900 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default D328Form;
